// @ts-nocheck
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box } from '@material-ui/core';
import {
  useAlert,
  useIfMounted,
  useOnPendingActionFinish,
  useOnChange,
  useOnUnmount,
} from '../../../common/hooks';
import { AlertSeverity } from '../../../common/providers';
import { EmptyState, Panel } from '../../../common/components';
import { selectPendingActions } from '../../../common/state/pending';
import { selectChannels, selectProducts } from '../../subscriptions/state/subscriptionSelectors';
import { selectOrders, selectOrderDetails } from '../state/orderSelectors';
import {
  fetchOrders,
  fetchOrder,
  deselectOrder,
  refundOrder,
  cancelOrder,
} from '../state/orderActions';
import {
  CHANGE_SUBSCRIPTION,
  CLOSE_SUBSCRIPTION,
  CREATE_SUPPORT_PRODUCT,
  REACTIVATE_SUBSCRIPTION,
} from '../../subscriptions/state/subscriptionActionTypes';
import OrderList from './partials/list/OrderList';
import OrderDetails from './partials/details/OrderDetails';
import { StringUtils } from '../../../common/utils';

const OrderStatus = {
  All: 'all',
  Active: 'active',
};

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    minHeight: '500px',
  },
}));

function OrdersPage() {
  const [showActive, setShowActive] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [t] = useTranslation();
  const { userId } = useParams();
  const { showAlert } = useAlert();
  const classes = useStyles();
  const pendingActions = useSelector(selectPendingActions);
  const orders = useSelector(selectOrders);
  const orderDetails = useSelector(selectOrderDetails);
  const channels = useSelector(selectChannels);
  const products = useSelector(selectProducts);
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  const handleFetchOrders = useCallback(
    async page => {
      try {
        await dispatch(
          fetchOrders(userId, showActive ? OrderStatus.Active : OrderStatus.All, page)
        );
      } catch (e) {
        showAlert({
          title: t('order.ordersFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, showActive, showAlert, t, userId]
  );

  const handleSelectOrder = useCallback(
    async params => {
      try {
        await dispatch(fetchOrder(params.row.id, params.row.type));
        ifMounted(() => setShowDetails(true));
      } catch (e) {
        showAlert({
          title: t('order.orderFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, ifMounted, showAlert, t, userId]
  );

  const handleCancelOrder = useCallback(async () => {
    try {
      await dispatch(cancelOrder(userId, orderDetails.order.orderId));
      await dispatch(
        fetchOrders(userId, showActive ? OrderStatus.Active : OrderStatus.All, orders?.page)
      );
      showAlert({
        title: t('common.success'),
        message: t('order.orderCancelSuccess'),
        severity: AlertSeverity.Success,
      });
    } catch (e) {
      showAlert({
        title: t('order.orderCancelError'),
        message: e.message,
        severity: AlertSeverity.Error,
      });
    }
  }, [dispatch, orderDetails, orders, showActive, showAlert, t, userId]);

  const handleRefundOrder = useCallback(
    async amount => {
      try {
        await dispatch(refundOrder(userId, orderDetails.order.orderId, amount));
        await dispatch(
          fetchOrders(userId, showActive ? OrderStatus.Active : OrderStatus.All, orders?.page)
        );
        showAlert({
          title: t('common.success'),
          message: t('order.orderRefundSuccess'),
          severity: AlertSeverity.Success,
        });
      } catch (e) {
        showAlert({
          title: t('order.orderRefundError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        throw e; // Rethrow to catch later
      }
    },
    [dispatch, orderDetails, orders, showActive, showAlert, t, userId]
  );

  useOnUnmount(() => {
    if (orderDetails) {
      dispatch(deselectOrder());
    }
  });

  useOnChange(showDetails, () => {
    if (!showDetails) {
      dispatch(deselectOrder());
    }
  });

  useOnChange(showActive, () => {
    handleFetchOrders(1);
  });

  useOnPendingActionFinish(
    [CHANGE_SUBSCRIPTION, CLOSE_SUBSCRIPTION, REACTIVATE_SUBSCRIPTION, CREATE_SUPPORT_PRODUCT],
    () => {
      handleFetchOrders(orders?.page);
    }
  );

  const getSum = (order) => {
    if (order.type === 'DISCOUNT_PERCENT') {
      return StringUtils.getFormattedSum(order.product.price * (100 - order.sum) / 100);
    } else if (order.type === 'DISCOUNT_AMOUNT') {
      return StringUtils.getFormattedSum(order.product.price - order.sum);
    } else {
      return StringUtils.getFormattedSum(order.sum);
    }
  };

  return (
    <Box id="Orders-container" className={classes.container}>
      <Panel id="Orders-panel" variant="contained">
        {!orders ? (
          <EmptyState variant="unavailable" message={t('order.ordersNotAvailable')} />
        ) : (
          <>
            {showDetails && orderDetails ? (
              <OrderDetails
                order={orderDetails}
                channels={_.defaultTo(channels, [])}
                products={_.defaultTo(products, [])}
                onRefundOrder={handleRefundOrder}
                onCancelOrder={handleCancelOrder}
                onHideDetails={() => setShowDetails(false)}
                actionsDisabled={!_.isEmpty(pendingActions)}
                getSum={getSum}
              />
            ) : (
              <OrderList
                data={orders}
                showActive={showActive}
                onFetchData={handleFetchOrders}
                onSelectOrder={handleSelectOrder}
                onFilterChange={() => setShowActive(!showActive)}
                actionsDisabled={!_.isEmpty(pendingActions)}
                getSum={getSum}
              />
            )}
          </>
        )}
      </Panel>
    </Box>
  );
}

export default OrdersPage;
